import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  #reviewsObserver = new MutationObserver(() => this.#positionReviews());

  #mobileLayout = window.matchMedia('(max-width: 749px)');

  connectedCallback() {
    super.connectedCallback();
    this.#reviewsObserver.observe(this, { childList: true, subtree: true });
    this.#mobileLayout.addEventListener('change', this.#positionReviews);
    this.#positionReviews();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    // Also listen for CartAddEvent to auto-open (already present)
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#reviewsObserver.disconnect();
    this.#mobileLayout.removeEventListener('change', this.#positionReviews);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    this.#historyAbortController?.abort();
  }

  #handleCartUpdate = (event) => {
    // Ensure we have a section ID to re-render
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    // Re-render the cart drawer section to reflect latest cart state
    sectionRenderer.renderSection(sectionId, { cache: false });
  };

  #positionReviews = () => {
    const widget = this.querySelector('#jdgm-cart-drawer-widget');
    const content = this.querySelector('.cart-drawer__content');
    const summary = content?.querySelector('.cart-drawer__summary');
    const items = content?.querySelector('.cart-drawer__items');
    if (!widget || !content || !summary || !items) return;

    if (this.#mobileLayout.matches) {
      if (widget.previousElementSibling !== summary) widget.before(summary);
    } else if (summary.parentElement === items) {
      content.append(summary);
    }
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
